import * as ko from 'knockout';
import $ from 'jquery';
import { isNil } from 'lodash-es';

import { SUPPORTED_ACCOUNTING_CURRENCIES } from '@/config/env';
import { ApiService } from '@/features/api';
import { AccountingSoftwareIntegrationEventsServiceEventType } from '@/legacy/features/companies/accountingSoftware/analytics/helper';
import { Instance as companyService, PermissionId } from '@/legacy/services/company';
import CompanyEventService from '@/legacy/services/companyEventService';
import * as viewModels from '@/legacy/viewmodels/viewModels';

import { apiUrl } from '../api/helpers';
import AccountingSoftwareBillAttachmentDetails from '../types/AccountingSoftwareBillAttachmentDetails';
import AccountingSoftwareBillViewModel from '../types/AccountingSoftwareBillViewModel';
import AttachmentDetails from '../types/AttachmentDetails';
import Bill from '../types/Bill';
import FileAttachment from '../types/FileAttachment';
import Invoice from '../types/Invoice';
import { SupportedAccountingSoftware } from '../types/SupportedAccountingSoftware';
import { SyncState } from '../types/SyncState';
import SyncType from '../types/SyncType';

import { Instance as accountingSoftwareService } from './accountingSoftware';

type ResyncOptions = {
  /** Type of synchronization (default: auto) */
  syncType?: SyncType;
  /** Synchronize bills (default: true) */
  syncBills?: boolean;
  /** Synchronize invoices (default: true) */
  syncInvoices?: boolean;
};

export class CompanyAccountingSoftwareService {
  private companyService = companyService;

  private accountingSoftwareService = accountingSoftwareService;

  public static billsLoadingError = ko.observable<string>('');

  public static newBills = ko.observableArray<Bill>();

  public static invoicesLoadingError = ko.observable<string>('');

  public static newInvoices = ko.observableArray<Invoice>();

  /**
   * Sync status of the company accounting software
   *
   * WARNING: Sets state to idle when the sync is complete and adds special error states
   * @deprecated use `syncState` instead
   */
  public syncStatus = ko.observable<SyncState>('idle');

  /**
   * Sync state of the company accounting software
   */
  public syncState = ko.observable<SyncState>('idle');

  public lastSyncedAt = ko.observable<Date>();

  public HasUnsupportedBills = ko.observable<boolean>(false);

  public HasUnsupportedInvoices = ko.observable<boolean>(false);

  private get companyId(): string {
    return this.companyService.CompanyId();
  }

  /** Accounting software package type observable for current company */
  public softwareTypeObservable = ko.pureComputed({
    read: () => {
      const companyMembership = this.companyService.membership();
      if (companyMembership === undefined || !ko.isObservable(companyMembership.company)) {
        return undefined;
      }
      const retVal = companyMembership.company().accountingSoftware();
      return retVal;
    },
    deferEvaluation: true,
  });

  /** Accounting software package observable for current company */
  public softwarePackageObservable = ko.pureComputed({
    read: () => {
      const id = this.softwareTypeObservable();
      return id ? this.accountingSoftwareService.GetPackage(id) : undefined;
    },
    deferEvaluation: true,
  });

  /** Accounting software package for current company */
  public get softwarePackage() {
    return this.softwarePackageObservable();
  }

  /** Check if company has accounting software connected and configured */
  public hasSoftwareConnected: KnockoutComputed<boolean> = ko.pureComputed({
    read: () => !isNil(this.softwarePackageObservable()),
    deferEvaluation: true,
  });

  /**
   * Resync company accounting software, along with bills and invoices (by default),
   * if allowed by current user permissions
   * @param options {@link ResyncOptions}
   * @returns Promise that resolves when the sync is complete
   */
  public resync({
    syncType = SyncType.Auto,
    syncBills = true,
    syncInvoices = true,
  }: ResyncOptions = {}) {
    if (!this.hasSoftwareConnected()) {
      return undefined;
    }

    let syncBillsAccordingToPermissions = syncBills;
    let syncInvoicesAccordingToPermissions = syncInvoices;
    if (!this.companyService.HasPermissionTo(PermissionId.PAYMENT_ADD_EDIT)) {
      syncBillsAccordingToPermissions = false;
      syncInvoicesAccordingToPermissions = false;
    }

    this.clearSelectedPayments();

    this.syncStatus('syncing');
    this.syncState('syncing');

    // See `if (!this.hasSoftwareConnected())` above.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.softwarePackage!.SyncData(syncType)
      .then(() => (syncBillsAccordingToPermissions ? this.UpdateBills() : Promise.resolve()))
      .then(() => (syncInvoicesAccordingToPermissions ? this.UpdateInvoices() : Promise.resolve()))
      .then(() => {
        const state = 'connected';

        this.syncStatus('idle');
        this.syncState(state);
        this.fireAccountingSoftwareSyncEvent(state);
        this.lastSyncedAt(new Date());
      })
      .catch((errorInfo) => {
        const state = 'error';

        this.syncStatus(state);
        this.syncState(state);
        this.fireAccountingSoftwareSyncEvent(
          errorInfo?.type === 'expired.oauth' ? 'expired' : state
        );

        this.clearSelectedPayments();

        if (
          errorInfo &&
          (errorInfo.type === 'expired.oauth' || errorInfo.type === 'error.tooManyRequests')
        ) {
          this.syncStatus(errorInfo.type);
        }
      });
  }

  public RefreshInvoices() {
    if (!this.hasSoftwareConnected()) {
      return undefined;
    }

    return this.UpdateInvoices();
  }

  public RefreshBills() {
    if (!this.hasSoftwareConnected()) {
      return undefined;
    }

    return this.UpdateBills();
  }

  /** Disconnect company from accounting software */
  public async disconnect(): Promise<void> {
    if (!this.hasSoftwareConnected()) {
      return;
    }

    // See `if (!this.hasSoftwareConnected())` above.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const accountingPlatform = this.softwarePackage!.id;

    await ApiService.remove(apiUrl(this.companyId));

    this.syncState('unset');
    this.lastSyncedAt(undefined);

    this.clearSelectedPayments();

    CompanyEventService.emit(
      AccountingSoftwareIntegrationEventsServiceEventType.AccountingPlatformDisconnected,
      {
        accountingPlatform,
        state: 'unlinked',
      }
    );
  }

  /**
   * Reset company accounting software sync state and clear selected payments
   */
  public resetSyncState() {
    this.syncState('idle');
    this.lastSyncedAt(undefined);

    this.clearSelectedPayments();
  }

  /**
   * Expire company accounting software sync state for current
   * company and clear selected payments
   */
  public expireSyncState() {
    this.syncState('expired');

    this.clearSelectedPayments();
  }

  /**
   * Clear selected bills and invoices
   */
  private clearSelectedPayments() {
    CompanyAccountingSoftwareService.newBills.removeAll();
    CompanyAccountingSoftwareService.newInvoices.removeAll();

    this.HasUnsupportedBills(false);
    this.HasUnsupportedInvoices(false);
  }

  private async UpdateBills(): Promise<void> {
    const { newBills } = CompanyAccountingSoftwareService;
    const { billsLoadingError } = CompanyAccountingSoftwareService;

    if (!this.hasSoftwareConnected()) {
      return;
    }

    try {
      // See `if (!this.hasSoftwareConnected())` above.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const bills = await this.softwarePackage!.GetBills();

      const supportedBills = bills.filter((checkBill) =>
        SUPPORTED_ACCOUNTING_CURRENCIES.includes(checkBill.currency)
      );

      if (supportedBills.length !== bills.length) {
        this.HasUnsupportedBills(true);
      }

      if (this.hasSoftwareConnected()) {
        // grab currently selected bills
        const payableBills = newBills().filter((checkBill) => checkBill.pay());

        newBills.removeAll();
        newBills(supportedBills);

        // re-select bills that were previously selected
        payableBills.forEach((payableBill) => {
          newBills().forEach((checkBill) => {
            if (checkBill.id === payableBill.id) {
              checkBill.pay(true);
            }
          });
        });
      }
    } catch (error) {
      billsLoadingError(error?.type ?? 'error');
      throw error;
    }
  }

  private async UpdateInvoices(): Promise<void> {
    const { newInvoices } = CompanyAccountingSoftwareService;
    const { invoicesLoadingError } = CompanyAccountingSoftwareService;

    // if accounting software doesn't support invoices we'll skip the invoice sync
    if (!this.softwarePackage?.supportsInvoices) {
      return;
    }

    try {
      const invoices = await this.softwarePackage.GetInvoices();

      const supportedInvoices = invoices.filter((checkInvoice) =>
        SUPPORTED_ACCOUNTING_CURRENCIES.includes(checkInvoice.currency)
      );

      if (supportedInvoices.length !== invoices.length) {
        this.HasUnsupportedInvoices(true);
      }

      if (this.hasSoftwareConnected()) {
        // grab currently selected invoices
        const payableInvoices = newInvoices().filter((checkInvoice) => checkInvoice.pay());
        newInvoices.removeAll();
        newInvoices(supportedInvoices);

        // re-select invoices that were previously selected
        payableInvoices.forEach((payableInvoice) => {
          newInvoices().forEach((checkInvoice) => {
            if (checkInvoice.id === payableInvoice.id) {
              checkInvoice.pay(true);
            }
          });
        });
      }
    } catch (error) {
      invoicesLoadingError(error?.type ?? 'error');
      throw error;
    }
  }

  public getAttachments(paymentId: string): Promise<Array<FileAttachment>> {
    return ApiService.get(apiUrl(this.companyId, `payments/${paymentId}/bill/attachments`));
  }

  public getPaymentRequestAttachments(paymentRequestId: string): Promise<Array<AttachmentDetails>> {
    if (!paymentRequestId) {
      return Promise.resolve([]);
    }

    return ApiService.get<Array<AttachmentDetails>>(
      apiUrl(this.companyId, `paymentRequests/${paymentRequestId}/attachments`)
    );
  }

  public getAttachmentsForBillId(
    billId: string
  ): Promise<Array<AccountingSoftwareBillAttachmentDetails>> {
    if (!billId) {
      return Promise.resolve([]);
    }

    const softwarePackage = this.softwarePackageObservable();
    if (
      softwarePackage == null ||
      // We don't support attachments for QBD, but there will still be a defined billId value.
      softwarePackage.id === SupportedAccountingSoftware.QuickBooksDesktop
    ) {
      return Promise.resolve([]);
    }

    return ApiService.get<Array<AccountingSoftwareBillAttachmentDetails>>(
      apiUrl(this.companyId, `/bills/${billId}/attachments`)
    );
  }

  public getAttachmentsForInvoiceId(
    invoiceId: string
  ): Promise<Array<AccountingSoftwareBillAttachmentDetails>> {
    return ApiService.get<Array<AccountingSoftwareBillAttachmentDetails>>(
      apiUrl(this.companyId, `/invoices/${invoiceId}/attachments`)
    );
  }

  public mapBillsToPaymentImport(
    bills: Array<any>
  ): JQueryPromise<Array<viewModels.PaymentImportViewModel>> {
    return $.Deferred<Array<viewModels.PaymentImportViewModel>>((dfd) => {
      ApiService.post<Array<viewModels.PaymentImportViewModel>>(
        apiUrl(this.companyId, 'mapBillsToPaymentImport'),
        { body: bills }
      )
        .then((paymentsData) => {
          dfd.resolve(paymentsData);
        })
        .catch((error) => {
          dfd.reject(error);
        });
    }).promise();
  }

  public mapInvoicesToInvoiceImport(
    invoices: Array<any>
  ): JQueryPromise<Array<viewModels.InvoiceImportViewModel>> {
    return $.Deferred<Array<viewModels.InvoiceImportViewModel>>((dfd) => {
      ApiService.post<Array<viewModels.InvoiceImportViewModel>>(
        apiUrl(this.companyId, 'mapInvoicesToPaymentRequestImport'),
        { body: invoices }
      )
        .then((paymentsData) => {
          dfd.resolve(paymentsData);
        })
        .catch((error) => {
          dfd.reject(error);
        });
    }).promise();
  }

  public getPaymentAccountingSoftwareBills(
    paymentId: string
  ): JQueryPromise<Array<AccountingSoftwareBillViewModel>> {
    return $.Deferred<Array<AccountingSoftwareBillViewModel>>((dfd) => {
      const url = apiUrl(this.companyId, `/payments/${paymentId}/accountingSoftwareBills`);
      ApiService.get<Array<AccountingSoftwareBillViewModel>>(url)
        .then((data) => {
          dfd.resolve(data);
        })
        .catch((error) => {
          dfd.reject(error);
        });
    }).promise();
  }

  public getAccountingSoftwareAvailability(accountingSoftwareName: string) {
    return ApiService.get(apiUrl(this.companyId, `/integrationEnabled/${accountingSoftwareName}`));
  }

  private fireAccountingSoftwareSyncEvent(state: SyncState) {
    const accountingPlatform = this.softwarePackage?.id;

    CompanyEventService.emit(
      AccountingSoftwareIntegrationEventsServiceEventType.AccountingPlatformSync,
      {
        buttonName: 'Accounting Platform Sync',
        accountingPlatform,
        state,
      }
    );
  }

  public requeueFailedCompanyAccountingSoftwareBill(billId: string) {
    const url = apiUrl(this.companyId, `/bills/${billId}/requeue`);
    return ApiService.post<boolean>(url);
  }
}

export const Instance = new CompanyAccountingSoftwareService();
